import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { catchError, exhaustMap, filter, finalize, interval, merge, of, startWith, switchMap, tap, fromEvent } from 'rxjs';

import { AccountStateService } from './account-state.service';
import { AuthApiService } from './auth-api.service';
import { AuthStoreService } from './auth-store.service';
import { OwnerAccountStatusResponse, OwnerStatusHistoryItem } from '@app/core/models';
import { buildOwnerStatusWidget } from '@app/core/constants/owner-status-content';

@Injectable({ providedIn: 'root' })
export class AccountStatusPollingService {
  private readonly authApi = inject(AuthApiService);
  private readonly authStore = inject(AuthStoreService);
  private readonly accountState = inject(AccountStateService);
  private readonly document = inject(DOCUMENT);

  private readonly ownerStatusSignal = signal<OwnerAccountStatusResponse | null>(null);
  private readonly historySignal = signal<OwnerStatusHistoryItem[]>([]);
  private readonly loadingSignal = signal(false);
  private readonly liveJustUnlockedSignal = signal(false);
  private started = false;
  private refreshInFlight = false;

  readonly ownerStatus = computed(() => this.ownerStatusSignal());
  readonly history = computed(() => this.historySignal());
  readonly loading = computed(() => this.loadingSignal());
  readonly liveJustUnlocked = computed(() => this.liveJustUnlockedSignal());

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;

    const focus$ = fromEvent(this.document.defaultView ?? window, 'focus');
    merge(interval(45000), focus$)
      .pipe(
        startWith(0),
        filter(() => this.authStore.role() === 'owner' && !this.accountState.isLive()),
        exhaustMap(() => this.refresh())
      )
      .subscribe();
  }

  requestRefresh(): void {
    this.refresh().subscribe();
  }

  refresh() {
    if (this.refreshInFlight) {
      return of(this.historySignal());
    }

    this.refreshInFlight = true;
    this.loadingSignal.set(true);

    return this.authApi.getOwnerAccountStatus().pipe(
      tap((status) => this.applyOwnerStatus(status)),
      switchMap(() => this.refreshHistory()),
      catchError(() => of([])),
      finalize(() => {
        this.refreshInFlight = false;
        this.loadingSignal.set(false);
      })
    );
  }

  refreshHistory() {
    return this.authApi.getOwnerStatusHistory().pipe(
      tap((history) => this.historySignal.set(history)),
      catchError(() => of([]))
    );
  }

  clearLiveUnlockedFlag(): void {
    this.liveJustUnlockedSignal.set(false);
  }

  private applyOwnerStatus(status: OwnerAccountStatusResponse): void {
    const wasLive = this.accountState.isLive();
    const widget = buildOwnerStatusWidget(status.status, status.widget);
    const isLive = status.isLive || status.status === 'Completed' || status.status === 'Live';

    this.ownerStatusSignal.set({
      ...status,
      isLive,
      isLimitedAccess: status.isLimitedAccess && !isLive,
      widget
    });

    this.accountState.setState({
      ...(this.accountState.state() ?? {
        userId: status.userId,
        role: 'owner',
        email: '',
        fullName: '',
        emailConfirmed: status.emailConfirmed
      }),
      userId: status.userId,
      emailConfirmed: status.emailConfirmed,
      onboardingStatus: status.status,
      isLive,
      isLimitedAccess: status.isLimitedAccess && !isLive,
      blockedReasonCode: status.blockedReasonCode ?? null,
      allowedSections: status.allowedSections,
      ownerStatusWidget: widget,
      onboardingCompleted: isLive
    });

    if (!wasLive && isLive) {
      this.liveJustUnlockedSignal.set(true);
    }
  }
}
